import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  gradients,
  iconSize,
  margin,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { FormProvider, useForm } from "react-hook-form";
import AgencyLocationField from "pages/components/AgencyLocationField";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { savePolicyTransaction } from "utils/api/policyTransactionApi";
import { showToast } from "controls/global/stewart-toast";

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  record: PolicyManagementRecord | null;
  onSubmit?: (reason?: string) => void;
}

// Small uppercase field label, matching the mockup's form-field captions.
const FieldLabel = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold1,
  color: colors.grey11,
  marginBottom: gaps.xsmall2,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

const ReadOnlyField = styled(TextField)({
  "& .MuiInputBase-root": {
    backgroundColor: colors.grey25,
  },
});

// Styled to match SerialNumberTransferDialog
// (src/pages/lookup/policy-details/dialogs/SerialNumberTransferDialog.tsx)
// so both Transfer dialogs share an identical look.
const TransferStyledDialog = styled(Dialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "calc(57.63vw)",
    minWidth: "calc(57.63vw)",
    display: "flex",
    justifyContent: "flex-start",
    padding: "36px 40px 40px 40px",
    overflowX: "auto",
  },
});

const TransferDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: gaps.large1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const TransferIconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const TransferCloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const TransferDialogContent = styled(Box)({
  padding: "24px 0",
});

const TransferContainerBox = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  "& .gridStyle": {
    paddingBottom: "0 !important" as any,
  },
});

const TransferDialogAction = styled(DialogActions)({
  justifyContent: "right",
  paddingBottom: padding.zero,
  paddingTop: padding.zero,
  paddingRight: padding.zero,
  paddingLeft: padding.zero,
});

const TransferSubmitBox = styled("div")({
  paddingTop: padding.xlarge1,
});

const TransferModal = ({ open, onClose, record, onSubmit }: TransferModalProps) => {
  const [newLocationData, setNewLocationData] = useState<CompanyLocation>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      newLocationFullDisplayName: "",
    },
  });
  const { setValue, reset } = methods;

  const handleClose = () => {
    setNewLocationData(undefined);
    reset({ newLocationFullDisplayName: "" });
    onClose();
  };

  const handleSubmit = async () => {
    if (!newLocationData?.locationFullDisplayName || !record?.PolicyTransactionID) return;
    setIsSubmitting(true);
    const ok = await savePolicyTransaction({
      PolicyTransactionID: record.PolicyTransactionID,
      CompanyID: newLocationData.companyID,
      CompanyName: newLocationData.companyName,
      CompanyLegacyID: newLocationData.companyLegacyID,
      LocationUniqueID: newLocationData.locationUniqueID,
      LocationDisplayName: newLocationData.locationDisplayName,
      LocationReportingID: newLocationData.locationReportingID,
      LocationAgencyCompanyID: newLocationData.locationAgencyCompanyID,
    });
    setIsSubmitting(false);
    if (!ok) {
      showToast({ type: "error", message: "Failed to transfer the record." });
      return;
    }
    showToast({ type: "success", message: "Transfer completed successfully." });
    onSubmit?.(newLocationData.locationFullDisplayName);
    setNewLocationData(undefined);
    reset({ newLocationFullDisplayName: "" });
    onClose();
  };

  const clearAgencyLocation = () => {
    setValue("newLocationFullDisplayName", "");
    setNewLocationData(undefined);
  };

  const handleLocationChange = (location: CompanyLocation | string) => {
    if (!location || typeof location === "string") return;
    setValue("newLocationFullDisplayName", location?.locationFullDisplayName || "");
    setNewLocationData(location);
  };

  const handleLocationBlur = (event: React.ChangeEvent<any>) => {
    const newInputValue = event?.target?.value;
    if (
      newLocationData === undefined ||
      !newInputValue ||
      newInputValue.toLowerCase() !== newLocationData?.locationFullDisplayName?.toLowerCase()
    ) {
      clearAgencyLocation();
    }
  };

  return (
    <TransferStyledDialog open={open} onClose={handleClose}>
      <TransferDialogTitle>
        <TransferIconContainer>
          <SwapHorizIcon />
        </TransferIconContainer>
        <Typography>Transfer</Typography>
        <TransferCloseIconContainer>
          <IconButton className="iconButton" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </TransferCloseIconContainer>
      </TransferDialogTitle>
      <TransferDialogContent>
        <FormProvider {...methods}>
          <TransferContainerBox>
            <Box className="gridStyle">
              <FieldLabel>Policy Number</FieldLabel>
              <ReadOnlyField
                fullWidth
                size="small"
                value={record?.PolicyNumber || "---"}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box className="gridStyle">
              <FieldLabel>Current Agency/Legacy ID (Location)</FieldLabel>
              <ReadOnlyField
                fullWidth
                size="small"
                value={record?.LocationDisplayName || "---"}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box className="gridStyle">
              <FieldLabel>
                Receiving Agency/Legacy ID (Location){" "}
                <span style={{ color: colors.red01 }}>*</span>
              </FieldLabel>
              <AgencyLocationField
                label=""
                placeholder="Select Receiving Agency/Legacy ID (Location)"
                name="newLocationFullDisplayName"
                onChange={handleLocationChange}
                onBlur={handleLocationBlur}
                onClear={clearAgencyLocation}
                excludeIds={record?.LocationUniqueID ? [record.LocationUniqueID] : undefined}
                excludeDisplayNames={record?.LocationDisplayName ? [record.LocationDisplayName] : undefined}
              />
            </Box>
          </TransferContainerBox>
        </FormProvider>
        <TransferSubmitBox>
          <TransferDialogAction>
            <ActionButton
              variant="outlined"
              color="secondary"
              style={{ marginRight: margin.large }}
              onClick={handleClose}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="outlined"
              color="primary"
              disabled={!newLocationData?.locationFullDisplayName || isSubmitting}
              onClick={handleSubmit}
            >
              Transfer
            </ActionButton>
          </TransferDialogAction>
        </TransferSubmitBox>
      </TransferDialogContent>
    </TransferStyledDialog>
  );
};

export default TransferModal;
